import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Seo — per-route SEO injector.
 *
 * Updates <title>, <meta description>, canonical URL, Open Graph and Twitter
 * tags client-side so Google's mobile crawler indexes the right metadata for
 * each public route.
 *
 * IMPORTANT: public/index.html no longer hardcodes a canonical or a
 * SoftwareApplication JSON-LD block (those used to appear on every page,
 * including legal/login/register, which Semrush flagged as "Multiple
 * canonical URLs" and "structured data with markup errors" — Feb 2026 SEO
 * lockdown). Every route MUST mount this Seo component to inject its own
 * per-page canonical + (optional) structured data.
 *
 * Props:
 *   title       — full page title (~55-60 chars)
 *   description — meta description (~150-160 chars)
 *   path        — route path, e.g. "/demo" (used for canonical + og:url)
 *   image       — absolute or root-relative image URL for OG/Twitter card
 *   jsonLd      — single JSON-LD object OR an array of JSON-LD objects.
 *                 Each is rendered as its own <script type="application/ld+json">
 *                 so multiple Schema.org types (e.g. SoftwareApplication +
 *                 FAQPage on the landing page) can coexist cleanly.
 *   noindex     — when true, emits <meta name="robots" content="noindex, nofollow">.
 *                 Used by auth-flow pages (login, register, forgot-password)
 *                 so they don't dilute search rankings or compete with /.
 */
const SITE = "https://vidyaai.study";

export default function Seo({ title, description, path = "/", image, jsonLd, noindex = false }) {
  const canonical = `${SITE}${path}`;
  const ogImage = image
    ? (image.startsWith("http") ? image : `${SITE}${image}`)
    : `${SITE}/og-vidyaai.png`;

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : (jsonLd ? [jsonLd] : []);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdArray.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
